import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { I18nProvider, useI18n } from "@/contexts/I18nContext";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { ChatWidget } from "@/components/ChatWidget";
import { NotFoundState } from "@/components/StateViews";
import { adScriptPresent } from "@/lib/ads";

// The admin panel (and recharts, which only its dashboard uses) loads on demand,
// so public visitors never download it.
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminBooks = lazy(() => import("@/pages/admin/AdminBooks"));
const AdminAuthors = lazy(() => import("@/pages/admin/AdminAuthors"));
const AdminPublishers = lazy(() => import("@/pages/admin/AdminPublishers"));
const AdminReviews = lazy(() => import("@/pages/admin/AdminReviews"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminAudit = lazy(() => import("@/pages/admin/AdminAudit"));
const AdminThreads = lazy(() => import("@/pages/admin/AdminThreads"));

import Home from "@/pages/Home";
import Books from "@/pages/Books";
import BookDetail from "@/pages/BookDetail";
import Authors from "@/pages/Authors";
import AuthorDetail from "@/pages/AuthorDetail";
import Publishers from "@/pages/Publishers";
import PublisherDetail from "@/pages/PublisherDetail";
import Search from "@/pages/Search";
import Profile from "@/pages/Profile";
import AuthCallback from "@/pages/AuthCallback";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Discussions from "@/pages/Discussions";
import DiscussionDetail from "@/pages/DiscussionDetail";
import InfoPage from "@/pages/InfoPage";


const SkipLink = () => {
  const { t } = useI18n();
  return (
    <a href="#main" data-testid="skip-link"
      className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[60] focus:rounded-[var(--radius)] focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground">
      {t("skip_to_content")}
    </a>
  );
};

const PublicShell = () => (
  <div className="min-h-screen flex flex-col paper-bg">
    <SkipLink />
    <Navbar />
    {/* min-height: the footer starts below the fold, so it never jumps down as content loads. */}
    <main id="main" tabIndex={-1} className="flex-1 min-h-[100svh] outline-none"><Outlet /></main>
    <Footer />
    <ChatWidget />
  </div>
);

// The admin panel never shares a document with third-party ad code: arriving
// here by in-app navigation (e.g. Back from a public page that loaded AdSense)
// reloads into a fresh page, where the ad script is never added.
const AdminDocument = ({ children }) => {
  if (adScriptPresent()) { window.location.reload(); return null; }
  return <Suspense fallback={<div className="admin-theme min-h-screen bg-background" />}>{children}</Suspense>;
};

const AppRouter = () => {
  return (
    <Routes>
      {/* Auth callback - handled by Supabase OAuth redirect */}
      <Route path="/auth-callback" element={<AuthCallback />} />

      {/* Admin (isolated, no public nav) */}
      <Route path="/control-panel/login" element={<AdminDocument><AdminLogin /></AdminDocument>} />
      <Route path="/control-panel" element={<AdminDocument><AdminAuthProvider><AdminLayout /></AdminAuthProvider></AdminDocument>}>
        <Route index element={<AdminDashboard />} />
        <Route path="books" element={<AdminBooks />} />
        <Route path="authors" element={<AdminAuthors />} />
        <Route path="publishers" element={<AdminPublishers />} />
        <Route path="reviews" element={<AdminReviews />} />
        <Route path="threads" element={<AdminThreads />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="audit" element={<AdminAudit />} />
      </Route>

      {/* Public */}
      <Route element={<PublicShell />}>
        <Route path="/" element={<Home />} />
        <Route path="/books" element={<Books />} />
        <Route path="/books/:slug" element={<BookDetail />} />
        <Route path="/authors" element={<Authors />} />
        <Route path="/authors/:slug" element={<AuthorDetail />} />
        <Route path="/publishers" element={<Publishers />} />
        <Route path="/publishers/:slug" element={<PublisherDetail />} />
        <Route path="/search" element={<Search />} />
        <Route path="/discussions" element={<Discussions />} />
        <Route path="/discussions/:threadId" element={<DiscussionDetail />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/about" element={<InfoPage page="about" />} />
        <Route path="/contact" element={<InfoPage page="contact" />} />
        <Route path="/privacy" element={<InfoPage page="privacy" />} />
        <Route path="/terms" element={<InfoPage page="terms" />} />
        <Route path="*" element={<NotFoundState />} />
      </Route>
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster richColors position="top-center" />
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}
export default App;
